import { create } from 'zustand';
import { api } from '../services/apiService';
import type { ActivityEvent } from '../types/activity';

interface ActivityFilters {
  type: string;
  status: string;
  hours: number;
}

interface ActivityState {
  events: ActivityEvent[];
  lastFetchTime: string | null;
  loading: boolean;
  filters: ActivityFilters;
  fetchEvents: () => Promise<void>;
  fetchNewEvents: () => Promise<void>;
  setFilters: (filters: Partial<ActivityFilters>) => void;
}

const DEFAULT_FILTERS: ActivityFilters = {
  type: 'all',
  status: 'all',
  hours: 6,
};

export const useActivityStore = create<ActivityState>((set, get) => ({
  events: [],
  lastFetchTime: null,
  loading: false,
  filters: DEFAULT_FILTERS,

  fetchEvents: async () => {
    const { filters } = get();
    set({ loading: true });
    try {
      const events = await api.get<ActivityEvent[]>(
        `/activity/feed?hours=${filters.hours}`
      );
      set({
        events,
        lastFetchTime: new Date().toISOString(),
        loading: false,
      });
    } catch (err) {
      console.error('Failed to fetch activity events:', err);
      set({ loading: false });
    }
  },

  fetchNewEvents: async () => {
    const { lastFetchTime, events } = get();
    if (!lastFetchTime) {
      return get().fetchEvents();
    }
    try {
      const newEvents = await api.get<ActivityEvent[]>(
        `/activity/feed?since=${lastFetchTime}`
      );
      if (newEvents.length > 0) {
        const existingIds = new Set(events.map((e) => e.id));
        const unique = newEvents.filter((e) => !existingIds.has(e.id));
        set({
          events: [...unique, ...events],
          lastFetchTime: new Date().toISOString(),
        });
      }
    } catch (err) {
      console.error('Failed to fetch new events:', err);
    }
  },

  setFilters: (partial) => {
    set((state) => ({
      filters: { ...state.filters, ...partial },
    }));
  },
}));
